import { useRef, useState } from "react"

import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { createPortal } from "react-dom"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"

import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import Button from "components/button/Button"
import Link from "components/link/Link"

import { useConfigurator } from "./../configurator-v3/context"

import { AnimatePresence, motion } from "framer-motion"
import FocusTrap from "focus-trap-react"
import getMediaQuery from "css/breakpoints"

import Globe from "../globe/globe"
import ArticleSimple from "../articles/ArticleSimple"
import { bold } from "css/text"
import { buttonReset } from "css/buttons"
import { useNav } from "components/nav-v2/Wrapper"

const common = css`
  grid-row: 1;
  grid-column: -3;
  align-self: center;
  justify-self: end;

  height: 30px !important;
  font-size: 0.75rem !important;
`

const CTALink = styled(Link)`
  ${common};
  font-weight: 400 !important;
`

const GlobeModal = styled.div`
  background: #000;
  height: 100vh;
  width: 100vw;
  overflow: scroll;
`

const CTAButton = styled(Button)`
  ${common};
  font-weight: 400 !important;
`

const Action = styled(Button)`
  ${buttonReset}
  ${bold}
  display: flex;
  width: 100%;
  text-decoration: none;
  color: currentColor;
  border: 1px solid rgb(var(--grey));
  border-radius: 50px;
  padding: 0.5rem 1rem;
  justify-content: center;
  transition: color 300ms;
  align-items: center;

  > svg {
    margin-inline-end: 0.5rem;
  }

  @media (hover: hover) {
    &:hover {
      color: rgb(var(--green));
    }
  }

  text-wrap: nowrap;
`

const Close = styled(Button)`
  background: #fff !important;
  z-index: 999;
  --spacing: calc(var(--outer-margin) - var(--height) / 2);
  ${getMediaQuery("m")} {
    --spacing: calc(var(--outer-margin) - var(--grid-gap) / 2 - var(--height) / 2);
  }
  ${getMediaQuery("l")} {
    --spacing: calc((var(--outer-margin) - var(--grid-gap)) / 2 - var(--height) / 2);
  }

  inset-block-start: var(--spacing);
  inset-inline-end: var(--spacing);

  position: fixed;
`

const Underlay = styled(motion.div)`
  inset: 0;
  position: fixed;
  z-index: calc(var(--z-top, 10000));
`

function LinkCTA({ link }) {
  const navigation = useNavigation()

  const onCTAClick = e => {
    waTriggerEvent({
      eventName: "ctaSubNav",
      eventType: "Click",
      ctaInfo: {
        name: link.cta_name_tch || link.outlink_name,
        url: link.href || link.external,
      },
    })

    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  return (
    <CTALink {...link} onClick={onCTAClick} className='filled small opaque-green' icon='none'>
      {link.label}
    </CTALink>
  )
}

function ConfigureCTA({ menuV2, label, param, cta_name_tch, cta_name }) {
  //  const console = useConsole()
  //  console.verbose(cta_name_tch, "Config CTA")
  const nav = useNav()
  const dictionary = useDictionary()
  const { route, cta, page_type, launched } = useConfigurator()
  const rmcRegExp = /\/m[^-]*-[0-9]*$/g

  function onClick(e) {
    nav.collapseAll().then(() => {
      const currentLink = process.browser ? window.location.href : null
      const rmc = currentLink ? currentLink.match(rmcRegExp) : null
      waTriggerEvent({
        eventName: "ctaSubNav",
        eventType: "Click",
        ctaInfo: {
          name: "configure",
          url: currentLink,
        },
      })

      if (!param || !currentLink) return
      let new_param = param
      if (rmc) {
        const fullpath = `${param}${rmc}`
        const index = currentLink.indexOf(fullpath)
        if (index < 0) return
        launched.set(true)
        new_param = `${fullpath}/*`
      }
      cta.current = e?.target
      page_type.current = "family-subnav"
      route.set(new_param)
    })
  }

  return menuV2 ? (
    <Action className='actionBtn' icon='none' onPress={onClick} aria_label={dictionary.ariaLabelOpenConfigurator()}>
      {label}
    </Action>
  ) : (
    <CTAButton className='filled opaque-green small' icon='none' onPress={onClick} aria_label={dictionary.ariaLabelOpenConfigurator()}>
      {label}
    </CTAButton>
  )
}

function GlobeCTA({ menuV2, label, param, cta_name_tch, article_simple }) {
  const console = useConsole()
  const openButtonRef = useRef()
  const closeButtonRef = useRef()
  const buttonTrapRef = useRef()
  const dictionary = useDictionary()
  const locale = useLocale()
  const nav = useNav()

  const lang = locale?.current?.codes?.ws
  const bodyElement = process.browser && document.body

  const iframeUrl = `https://www.rolex.org/${lang}/environment/perpetual-planet/v7-globe`

  const [isOpen, setIsOpen] = useState(false)

  function onClose() {
    setIsOpen(false)
  }

  function onOpen() {
    nav.collapseAll().then(() => {
      const currentLink = process.browser && window ? window.location.href : ""
      waTriggerEvent({
        eventName: "ctaSubNav",
        eventType: "Click",
        ctaInfo: {
          name: "globe",
          url: currentLink,
        },
      })

      setIsOpen(true)
    })
  }

  function onKeyDownTrap(e) {
    console.log("onKeyDownTrap", e.key)
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault()
      closeButtonRef.current.focus()
    }
  }

  console.verbose(isOpen, "Globe CTA", article_simple)

  return (
    <>
      {menuV2 ? (
        <Action ref={openButtonRef} icon='none' aria-label={dictionary.ariaLabelOpenPopinModel()} className='menuV2' onPress={onOpen}>
          {label}
        </Action>
      ) : (
        <CTAButton ref={openButtonRef} icon='none' aria-label={dictionary.ariaLabelOpenPopinModel()} className='filled opaque-green small' onPress={onOpen}>
          {label}
        </CTAButton>
      )}
      {process.browser &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <FocusTrap focusTrapOptions={{ onDeactivate: onClose }}>
                <Underlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  <GlobeModal aria-modal='true' role='dialog' aria-labelledby='beautyshot-modal-heading' aria-describedby='beautyshot-modal-content'>
                    <Close ref={closeButtonRef} aria-label={dictionary.popinClose()} icon='close' className='icon' onPress={onClose}>
                      <span>{dictionary.popinClose()}</span>
                    </Close>
                    <ArticleSimple
                      texts={article_simple[0].texts}
                      className={[
                        article_simple[0].className,
                        article_simple[0].theme.theme,
                        article_simple[0].box_spacing.margin_top,
                        article_simple[0].box_spacing.margin_bottom,
                        article_simple[0].box_spacing.padding_top,
                        article_simple[0].box_spacing.padding_bottom,
                      ].join(" ")}
                      background={article_simple[0].background}
                      horizontal_align={article_simple[0].horizontal_align}
                      vertical_align={article_simple[0].vertical_align}
                      title_gradient={article_simple[0].title_gradient}
                      chapo_gradient={article_simple[0].chapo_gradient}
                    />
                    <Globe iframe_url={iframeUrl} />
                    <button ref={buttonTrapRef} onFocus={onKeyDownTrap} style={{ position: "absolute", bottom: 0, left: -9999 }} aria-hidden='true'></button>
                  </GlobeModal>
                </Underlay>
              </FocusTrap>
            )}
          </AnimatePresence>,
          bodyElement
        )}
    </>
  )
}

export default function Cta(props) {
  const { cta } = props

  const ctaItem = Array.isArray(cta) ? cta[0] : null
  const ctaObj = !Array.isArray(cta) ? cta || props : null

  return ctaItem?.link ? (
    <LinkCTA link={ctaItem.link} />
  ) : ctaItem?.config_launcher ? (
    <ConfigureCTA {...ctaItem.config_launcher} />
  ) : ctaItem?.globe_launcher ? (
    <GlobeCTA {...ctaItem.globe_launcher} />
  ) : ctaObj?.param !== "globe" ? (
    <ConfigureCTA menuV2 {...ctaObj} />
  ) : ctaObj ? (
    <GlobeCTA menuV2 {...ctaObj} />
  ) : null
}
